import type { Employment, PayInterval, Posting, StatedPay } from "@/lib/api/catalogue";
import { knownCountryName, namedCities } from "@/lib/posting/places";
import { postingDetailPath } from "@/lib/routes";
import { escapeHtml } from "./html";

/**
 * A posting's schema.org `JobPosting` data (Google's job posting
 * structured data), written into the posting page's head by ./postingPage.ts.
 * Everything in it is on the page itself, as Google requires. Pure.
 */

/**
 * How long a posting is believed once its board stops listing it. The same as
 * `SEEN_WITHIN_DAYS` in apps/catalogue/src/api/handlers/sitemaps.rs: what the sitemaps leave
 * out, the page must not offer as a job either.
 */
export const SEEN_WITHIN_DAYS = 3;

const DAY_MS = 86_400_000;

/** Google's word for each period a posting states pay for: `PayInterval` in catalogue_api. */
const UNIT_TEXT: Readonly<Record<PayInterval, string>> = {
    HOUR: "HOUR",
    DAY: "DAY",
    WEEK: "WEEK",
    MONTH: "MONTH",
    YEAR: "YEAR",
};

/** Google's word for each way of working. Only these two differ from ours. */
const EMPLOYMENT_TYPE: Readonly<Record<Employment, string>> = {
    FULL_TIME: "FULL_TIME",
    PART_TIME: "PART_TIME",
    CONTRACT: "CONTRACTOR",
    INTERNSHIP: "INTERN",
    TEMPORARY: "TEMPORARY",
};

/** The text as `<p>` paragraphs, one per blank-line break, each escaped. */
function paragraphs(text: string): string {
    return text
        .replaceAll("\r\n", "\n")
        .split(/\n\s*\n/)
        .map((paragraph) => paragraph.trim())
        .filter((paragraph) => paragraph !== "")
        .map((paragraph) => `<p>${escapeHtml(paragraph)}</p>`)
        .join("");
}

/** A currency as ISO 4217 writes it: three capital letters. Anything else is nothing. */
function currencyCode(currency: string | undefined): string | null {
    const code = typeof currency === "string" ? currency.trim().toUpperCase() : "";
    return /^[A-Z]{3}$/.test(code) ? code : null;
}

/** An instant as the catalogue writes it (RFC 3339), or nothing when it is no instant. */
function instant(value: string | null | undefined): string | null {
    if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}T/.test(value)) return null;
    return Number.isNaN(Date.parse(value)) ? null : value;
}

/**
 * Whether the posting's board listed it in the last `SEEN_WITHIN_DAYS`, the bound included as
 * the sitemaps include it. A date in the future is a clock ahead, not a dead board. A date that
 * cannot be read is old.
 */
export function seenLately(posting: Pick<Posting, "last_seen_at">, now: Date): boolean {
    const seenAt = instant(posting.last_seen_at);
    if (seenAt === null) return false;
    return now.getTime() - Date.parse(seenAt) <= SEEN_WITHIN_DAYS * DAY_MS;
}

/** A figure the board stated: a number above nothing. */
function isAmount(figure: number | null): figure is number {
    return figure !== null && Number.isFinite(figure) && figure > 0;
}

/**
 * The pay as a `MonetaryAmount`, or nothing. It needs a currency, an
 * amount and a period Google knows. One stated figure, or a range of one,
 * is a `value`; two different figures are a range.
 */
function baseSalary(pay: StatedPay | null): object | undefined {
    const currency = currencyCode(pay?.currency);
    if (!pay || currency === null || pay.interval === null) return undefined;
    if (!Object.hasOwn(UNIT_TEXT, pay.interval)) return undefined;
    const unitText = UNIT_TEXT[pay.interval];
    const first = isAmount(pay.min) ? pay.min : null;
    const second = isAmount(pay.max) ? pay.max : null;
    const amount = first ?? second;
    if (amount === null) return undefined;
    // A range runs from low to high, whichever way the board gave it.
    const quantity =
        first !== null && second !== null && first !== second
            ? { minValue: Math.min(first, second), maxValue: Math.max(first, second) }
            : { value: amount };
    return {
        "@type": "MonetaryAmount",
        currency,
        value: { "@type": "QuantitativeValue", ...quantity, unitText },
    };
}

/**
 * The posting's country: the one it states, else the one all its states are in. `San Francisco,
 * CA` states none, as two letters could be Canada. The sitemaps list the same postings: see
 * apps/catalogue/src/database/posting/list_sitemap.rs.
 */
function countryOf(posting: Pick<Posting, "country" | "regions">): string | null {
    if (posting.country) return posting.country;
    const countries = new Set(posting.regions.map((region) => region.slice(0, 2)));
    const [country] = countries;
    return countries.size === 1 && country ? country : null;
}

/**
 * A city as an address. The catalogue spells one with its state, `Folsom, CA`, or with its
 * country when it knows no state, `Norwich, United States`. Any other ending is the city's own.
 */
function cityAddress(city: string, country: string, regions: readonly string[]): object {
    const comma = city.lastIndexOf(", ");
    if (comma <= 0) return { addressLocality: city };
    const name = city.slice(0, comma);
    const ending = city.slice(comma + 2);
    if (regions.includes(`${country}-${ending}`)) {
        return { addressLocality: name, addressRegion: ending };
    }
    return { addressLocality: ending === knownCountryName(country) ? name : city };
}

/**
 * One place per city, each with the country. With no city named, one per state, or the country
 * alone when no state is named either.
 */
function jobLocation(
    cities: readonly string[],
    regions: readonly string[],
    country: string,
): object[] {
    const prefix = `${country}-`;
    const states = regions
        .filter((region) => region.startsWith(prefix))
        .map((region) => ({ addressRegion: region.slice(prefix.length) }));
    const addresses =
        cities.length > 0 ? cities.map((city) => cityAddress(city, country, regions)) : states;
    return (addresses.length > 0 ? addresses : [{}]).map((address) => ({
        "@type": "Place",
        address: { "@type": "PostalAddress", ...address, addressCountry: country },
    }));
}

/**
 * The posting's `JobPosting` data at `now`, or `null` when it gets none: a closed posting, one
 * its board has stopped listing, and one with no known country, no date or no text, which
 * Google requires.
 */
export function postingJsonLd(posting: Posting, siteUrl: string, now: Date): object | null {
    if (posting.closed_at !== null) return null;
    if (!seenLately(posting, now)) return null;
    const country = countryOf(posting);
    if (country === null) return null;
    // Google requires the date, and a text that is no date is worse than none.
    const datePosted = instant(posting.posted_at) ?? instant(posting.first_seen_at);
    if (datePosted === null) return null;
    const description = paragraphs(posting.description);
    if (description === "") return null;
    const remote = posting.work_setting === "REMOTE";
    const cities = namedCities(posting.cities);
    const salary = baseSalary(posting.pay);
    const employmentType =
        posting.employment && Object.hasOwn(EMPLOYMENT_TYPE, posting.employment)
            ? EMPLOYMENT_TYPE[posting.employment]
            : undefined;
    return {
        "@context": "https://schema.org",
        "@type": "JobPosting",
        title: posting.title,
        description,
        url: new URL(postingDetailPath(posting.posting_id), siteUrl).href,
        datePosted,
        hiringOrganization: {
            "@type": "Organization",
            name: posting.company,
            ...(posting.logo_url ? { logo: posting.logo_url } : {}),
        },
        identifier: {
            "@type": "PropertyValue",
            name: posting.company,
            value: posting.external_id,
        },
        directApply: false,
        ...(employmentType ? { employmentType } : {}),
        ...(salary ? { baseSalary: salary } : {}),
        // A remote job with no city names no place to be at.
        ...(remote && cities.length === 0
            ? {}
            : { jobLocation: jobLocation(cities, posting.regions, country) }),
        ...(remote
            ? {
                  jobLocationType: "TELECOMMUTE",
                  applicantLocationRequirements: { "@type": "Country", name: country },
              }
            : {}),
    };
}
